import { useMemo } from "react";
import { useSelector } from "react-redux";
import { AsyncProps } from "react-select/async";
import { FormikErrors } from 'formik';
import { components, GroupBase, InputProps } from "react-select";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GooglePlacesAutocomplete, { geocodeByPlaceId } from "react-google-places-autocomplete";
import clsx from "clsx";
import { InternationalAddressCountryTypes } from "@iddataweb/idw-common-ui/enums";
import { GoogleAddressComponentDisplay, SelectOption } from "@iddataweb/idw-common-ui/types";
import { getGooglePlacesAPIKey } from "@redux/selectors/verification-configuration-selectors";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { Logger } from "@utils/logger";
import { getAddressComponents, getFormattedAddress, getInitialLocation } from "@utils/address-util";
import { AXNUIInputErrorMessage } from "@iddataweb/idw-common-ui/containers";
import styles from "./international-address-select-input.less";

type InternationalAddressSelectInputProps = {
    googleAddressComponents: Array<GoogleAddressComponentDisplay>;
    title: string;
    attributeId: number;
    required: boolean;
    countryCode: InternationalAddressCountryTypes;
    fieldName: string;
    fieldError: string;
    fieldTouched: boolean;
    fieldIsInvalid: boolean;
    fieldShouldBeHighlighted: boolean;
    addressObject: { address: string; components: any };
    setAddressObject: ({ address, components }: { address: string; components: any }) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setHasGooglePlacesLoadError: (hasError: boolean) => void;
};

export const InternationalAddressSelectInput = (props: InternationalAddressSelectInputProps) => {
    const {
        googleAddressComponents,
        title,
        attributeId,
        required,
        countryCode,
        fieldName,
        fieldError,
        fieldTouched,
        fieldIsInvalid,
        fieldShouldBeHighlighted,
        addressObject,
        setAddressObject,
        setFieldValue,
        setFieldTouched,
        setHasGooglePlacesLoadError
    } = props;

    const googlePlacesAPIKey = useSelector(getGooglePlacesAPIKey);
    const content = useSelector(getLanguageContent);

    const onChange = async (newSelectOption: SelectOption<any>) => {
        const value = newSelectOption.value;
        if (value?.place_id) {
            try {
                const geocodeResult = await geocodeByPlaceId(value.place_id)
                if (geocodeResult?.[0]?.address_components && geocodeResult?.[0]?.formatted_address) {
                    const addressComponents = geocodeResult[0].address_components;
                    Logger.info('Raw Address Results: ', addressComponents);
                    const components = getAddressComponents(addressComponents);
                    const formattedAddress = getFormattedAddress(components, googleAddressComponents);
                    setAddressObject({ address: formattedAddress, components });
                    if (formattedAddress) {
                        await setFieldValue(fieldName, formattedAddress);
                    } else {
                        await setFieldValue(fieldName, '');
                    }
                    setFieldTouched(fieldName, true, true);
                }
            } catch (error) {
                console.log("Error in onChange function: ", error);
            }
        }
    };

    const onChangeRaw = async () => {
        await setFieldValue(fieldName, "");
        setFieldTouched(fieldName, true, true);
    };

    const onLoadFailed = (error: Error) => {
        console.log(error);
        Logger.error("Could not inject Google script", error);
        setHasGooglePlacesLoadError(true);
    };

    const inputComponent = (inputProps: InputProps) => {
        return (
            <components.Input
                {...inputProps}
                //@ts-ignore
                title={title}
                //@ts-ignore
                aria-required={required}
            />
        );
    };

    //todo - need to translate "No Options" message and "Loading..." message
    const selectProps: AsyncProps<SelectOption<any>, false, GroupBase<SelectOption<any>>> = {
        inputId: `attr${attributeId}-international-address`,
        placeholder: addressObject.address || content.validation.internationaladdress.required,
        onChange: onChange,
        //@ts-ignore
        onChangeRaw: onChangeRaw,
        components: {
            DropdownIndicator: null,
            Input: (props: InputProps) => inputComponent(props)
        },
        className: clsx("form-control", {
            'is-invalid': fieldIsInvalid,
            'highlighted-input': fieldShouldBeHighlighted
        }),
        classNamePrefix: "react-select"
    };

    const autocompletionRequest = {
        types: ["address"],
        componentRestrictions: {
            country: countryCode ? countryCode : InternationalAddressCountryTypes.US
        }
    };

    const onClickClear = async () => {
        setAddressObject(getInitialLocation(countryCode));
        await setFieldValue(fieldName, "");
        setFieldTouched(fieldName, true, true);
    };

    const className = useMemo(() => {
        return clsx(styles.internationalAddressSelectInput, addressObject.address ? styles.internationalAddressSelectInputWithData : "");
    }, [addressObject.address]);

    return (
        <div className={className}>
            {addressObject.address && (
                <div className={styles.clearButton} onClick={onClickClear}>
                    <FontAwesomeIcon id={`clear-address-button`} icon={faTimes} />
                </div>
            )}
            <GooglePlacesAutocomplete
                apiKey={googlePlacesAPIKey}
                autocompletionRequest={autocompletionRequest}
                debounce={300}
                key={addressObject.address}
                minLengthAutocomplete={3}
                selectProps={selectProps}
                onLoadFailed={onLoadFailed}
            />
            <AXNUIInputErrorMessage
                fieldError={fieldError}
                fieldTouched={fieldTouched}
                ignoreTouched={false}
            />
        </div>
    );
}